import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useRouter as useTanStackRouter } from '@tanstack/react-router'
import { useMemo } from 'react'

/**
 * `next/navigation` for the ported components. `router.refresh()` meant
 * "re-run the server loaders"; here it invalidates every query and the
 * route loaders, which is the same promise.
 */
export function useRouter() {
  const navigate = useNavigate()
  const router = useTanStackRouter()
  const queryClient = useQueryClient()
  return useMemo(
    () => ({
      push: (href: string) => void navigate({ to: href as never }),
      replace: (href: string) => void navigate({ to: href as never, replace: true }),
      back: () => window.history.back(),
      refresh: () => {
        void queryClient.invalidateQueries()
        void router.invalidate()
      },
    }),
    [navigate, router, queryClient],
  )
}

export function usePathname(): string {
  return useLocation().pathname
}

export function useSearchParams(): URLSearchParams {
  const search = useLocation().searchStr
  return useMemo(() => new URLSearchParams(search), [search])
}
